import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { displayTripStatus } from '../../utils/actingTripStatus';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Car,
  Search,
  Eye,
  Download,
  ChevronDown,
  ChevronUp,
  Users,
  UserCheck,
  Navigation,
} from 'lucide-react';
import { api } from '../../utils/api';
import RideMatchModal from '../../components/Trips/RideMatchModal';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import { cancelReasons } from '../../components/driverCancelReasons';

/*
    The Developers filter defaults to "Exclude", but the page remembers its filters in
    the browser (usePersistentFilters) and a saved "Include" beat that default forever —
    only the date fields ever expired. So admins who had once picked "Include
    Developers" kept seeing developer trips as the page's "default".

    One-time reset of that saved value; after it, choosing Include / Only still works
    and lasts as long as the other filters do (see clearOnExpiry below).
*/
const ACTING_TRIPS_FILTERS_KEY = 'acting-trips-filters';
(() => {
  try {
    const flag = `${ACTING_TRIPS_FILTERS_KEY}_developer_default_v1`;
    if (localStorage.getItem(flag)) return;
    const raw = localStorage.getItem(ACTING_TRIPS_FILTERS_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      if (saved && saved.developerOnly && saved.developerOnly !== 'Exclude') {
        saved.developerOnly = 'Exclude';
        localStorage.setItem(ACTING_TRIPS_FILTERS_KEY, JSON.stringify(saved));
      }
    }
    localStorage.setItem(flag, '1');
  } catch (e) {
    // Storage blocked or unreadable: the page falls back to its own defaults.
  }
})();

// TripLocationViewer component - moved outside to prevent recreation
const TripLocationViewer = React.memo(({ startLocation, endLocation, tripId }) => {
  const [showLocations, setShowLocations] = useState(false);
  const [startLocationName, setStartLocationName] = useState(null);
  const [endLocationName, setEndLocationName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getLocationName = async (location, type = '') => {
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      return null;
    }

    if (!latitude || !longitude) return null;

    try {
      const response = await api.post('/geocoding/reverse', { latitude, longitude });
      return response?.placeName || response?.data?.placeName || 'Location not found';
    } catch (error) {
      return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }
  };

  const handleViewLocations = async () => {
    if (startLocationName && endLocationName) {
      setShowLocations(prev => !prev);
      return;
    }
    if (showLocations) { setShowLocations(false); return; }

    setIsLoading(true);
    try {
      const promises = [];
      if (!startLocationName && startLocation) {
        promises.push(getLocationName(startLocation).then(name => ({ type: 'start', name })));
      }
      if (!endLocationName && endLocation) {
        promises.push(getLocationName(endLocation).then(name => ({ type: 'end', name })));
      }
      if (promises.length > 0) {
        const results = await Promise.all(promises);
        let newStartName = startLocationName;
        let newEndName = endLocationName;
        results.forEach(result => {
          if (result.type === 'start') newStartName = result.name;
          else if (result.type === 'end') newEndName = result.name;
        });
        setStartLocationName(newStartName);
        setEndLocationName(newEndName);
        setIsLoading(false);
        setTimeout(() => setShowLocations(true), 100);
      } else {
        setShowLocations(true);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      setTimeout(() => setShowLocations(true), 100);
    }
  };

  // A trip location arrives as [lng, lat] or { latitude, longitude }.
  const coordsOf = (location) => {
    let latitude, longitude;
    if (Array.isArray(location) && location.length >= 2) {
      longitude = location[0]; latitude = location[1];
    } else if (location && typeof location === 'object') {
      latitude = location.latitude; longitude = location.longitude;
    } else {
      return null;
    }
    latitude = Number(latitude); longitude = Number(longitude);
    if (!latitude || !longitude) return null;
    return { latitude, longitude };
  };

  const formatLocation = (location) => {
    const c = coordsOf(location);
    return c ? `${c.latitude.toFixed(4)}, ${c.longitude.toFixed(4)}` : 'Location not available';
  };

  // The exact point, not the rounded text shown: opens Google Maps with a pin on it.
  const mapsUrl = (location) => {
    const c = coordsOf(location);
    return c ? `https://www.google.com/maps/search/?api=1&query=${c.latitude},${c.longitude}` : null;
  };

  // A small navigation icon next to a coordinate that opens it in Google Maps.
  const OpenInMaps = ({ location, label }) => {
    const href = mapsUrl(location);
    if (!href) return null;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open ${label} in Google Maps`}
        aria-label={`Open ${label} in Google Maps`}
        className="ml-2 inline-flex items-center text-blue-600 hover:text-blue-800"
      >
        <Navigation className="h-3 w-3" />
      </a>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="space-y-1 text-sm">
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(startLocation)}</span>
            <OpenInMaps location={startLocation} label="pickup" />
          </div>
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-red-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(endLocation)}</span>
            <OpenInMaps location={endLocation} label="drop" />
          </div>
        </div>
        <button
          onClick={handleViewLocations}
          disabled={isLoading}
          className={`px-3 py-1 text-xs rounded-md transition-colors ${isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            }`}
        >
          {isLoading ? (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </button>
      </div>

      {showLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button onClick={() => setShowLocations(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">✕</button>
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Trip Locations</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-green-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">Start Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">{startLocationName || 'Loading...'}</p>
                <p className="text-xs text-gray-500 font-mono">{formatLocation(startLocation)}<OpenInMaps location={startLocation} label="start location" /></p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-red-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">End Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">{endLocationName || 'Loading...'}</p>
                <p className="text-xs text-gray-500 font-mono">{formatLocation(endLocation)}<OpenInMaps location={endLocation} label="end location" /></p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowLocations(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const ActingTrip = () => {
  const [rideMatchTrip, setRideMatchTrip] = useState(null);
  const [period, setPeriod] = useState('');
  const [regionalOffices, setRegionalOffices] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Individual stats filters
  const [completedRidesPeriod, setCompletedRidesPeriod] = useState('thisMonth');
  const [totalRidesPeriod, setTotalRidesPeriod] = useState('thisMonth');
  const [todayRidesStatus, setTodayRidesStatus] = useState('');
  const [yesterdayRidesStatus, setYesterdayRidesStatus] = useState('');
  const [completedRidesStatus] = useState('COMPLETED');
  const [totalRidesStatus, setTotalRidesStatus] = useState('');

  // The customer's car. An acting trip's own `vehicleType` is its pricing package
  // (ACTING_HOURLY ...), which is not what an admin wants in a "vehicle" column.
  const carTypeOf = (trip) => (
    trip?.actingDetails?.customerVehicle?.type
    || trip?.passangerVehicleType
    || trip?.actingVehicleType
    || trip?.vehicleType
    || ''
  );

  const defaultFilters = {
    search: '',
    status: '',
    paymentMethod: '',
    vehicleType: '',
    regionalOffice: '',
    paymentStatus: '',
    femaleOnly: false,
    developerOnly: 'Exclude',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 10,
    verificationNeeded: false,
    showCancelReason: false,
  };

  const {
    filters,
    handleFilterChange: originalHandleFilterChange,
    handlePageChange,
    clearFilters,
    updateFilters
  } = usePersistentFilters(defaultFilters, ACTING_TRIPS_FILTERS_KEY, {
    // Dates reset after the hook's 12-hour expiry; the Developers filter now does too, so
    // it returns to "Exclude" instead of staying on a past "Include".
    clearOnExpiry: ['startDate', 'endDate', 'approvedAtDate', 'period', 'developerOnly'],
  });

  const handleFilterChange = (key, value) => {
    originalHandleFilterChange(key, value);
  };

  // Always include isActingDriverTrip: true in actual API calls
  const effectiveFilters = { ...filters, isActingDriverTrip: true };

  const periodOptions = [
    { value: '', label: 'All Data' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'pastWeek', label: 'Past Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'PENDING', label: 'Waiting for Driver' },
    { value: 'ASSIGNED', label: 'Assigned' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'CANCELLED', label: 'Cancelled (All)' },
    { value: 'CANCELLED_BEFORE_PICKUP', label: 'Cancelled - Before Pickup' },
    { value: 'CANCELLED_AFTER_PICKUP', label: 'Cancelled - After Pickup' },
    { value: 'DROPPED', label: 'Dropped' },
    { value: 'PICKEDUP', label: 'Picked Up' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'failed', label: 'Driver NOT Accepted' }
  ];

  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['acting-trips-stats', completedRidesPeriod, totalRidesPeriod, todayRidesStatus, yesterdayRidesStatus, completedRidesStatus, totalRidesStatus, effectiveFilters.developerOnly],
    () => api.get('/trips/stats', {
      params: {
        completedRidesPeriod,
        totalRidesPeriod,
        todayRidesStatus,
        yesterdayRidesStatus,
        completedRidesStatus,
        totalRidesStatus,
        developerOnly: effectiveFilters.developerOnly,
        isActingDriverTrip: true
      }
    }),
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false
    }
  );

  const { data, isLoading, isFetching, error } = useQuery(
    ['acting-trips', effectiveFilters],
    () => api.get('/trips', { params: effectiveFilters }),
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false
    }
  );

  if (error) toast.error('Failed to load acting trips');

  React.useEffect(() => {
    const fetchRegionalOffices = async () => {
      try {
        const response = await api.get('/regional-offices', { params: { limit: 100, isActive: true } });
        if (response.data.success) setRegionalOffices(response.data.data.offices || []);
      } catch (error) {}
    };
    fetchRegionalOffices();
  }, []);

  const getFilterClasses = (filterValue, isCheckbox = false) => {
    const isActive = isCheckbox ? filterValue === true : (filterValue && filterValue !== '' && filterValue !== 'Exclude' && filterValue !== 'Include');
    const baseClasses = isCheckbox ? 'h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded' : 'input';
    if (isActive) {
      return isCheckbox
        ? `${baseClasses} ring-2 ring-blue-400 bg-blue-50`
        : `${baseClasses} border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200`;
    }
    return baseClasses;
  };

  const pagination = data?.data?.data?.pagination || {};

  const trips = React.useMemo(() => {
    let allTrips = data?.data?.data?.trips || [];
    if (filters.femaleOnly) {
      allTrips = allTrips.filter(trip => {
        if (trip.femaleOnly === true) return true;
        const passengerGender = trip.passengerGender || trip.passengerId?.gender;
        if (passengerGender === 'FEMALE' || passengerGender === 'female' || passengerGender === 'F') return true;
        const passengerName = trip.bookingForName || '';
        return passengerName.toLowerCase().includes('ms.') || passengerName.toLowerCase().includes('mrs.') || passengerName.toLowerCase().includes('miss');
      });
    }
    return allTrips;
  }, [data?.data?.data?.trips, filters.femaleOnly]);

  const stats = React.useMemo(() => {
    if (statsData?.data?.data) {
      return {
        todayRides: statsData.data.data.todayRides || 0,
        yesterdayRides: statsData.data.data.yesterdayRides || 0,
        completedRides: statsData.data.data.completedRides || 0,
        totalRides: statsData.data.data.totalRides || 0
      };
    }
    return { todayRides: 0, yesterdayRides: 0, completedRides: 0, totalRides: 0 };
  }, [statsData?.data?.data]);

  const handlePeriodChange = (newPeriod) => {
    try {
      setPeriod(newPeriod);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let startDate, endDate;

      switch (newPeriod) {
        case 'today':
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
          endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
          break;
        case 'thisWeek':
          const dayOfWeek = today.getDay();
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - dayOfWeek);
          startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), 0, 0, 0, 0);
          endDate = new Date(now);
          break;
        case 'pastWeek':
          const currentDayOfWeek = today.getDay();
          const daysToLastSunday = currentDayOfWeek + 7;
          const lastSunday = new Date(today);
          lastSunday.setDate(today.getDate() - daysToLastSunday);
          const lastSaturday = new Date(lastSunday);
          lastSaturday.setDate(lastSunday.getDate() + 6);
          startDate = new Date(lastSunday.getFullYear(), lastSunday.getMonth(), lastSunday.getDate(), 0, 0, 0, 0);
          endDate = new Date(lastSaturday.getFullYear(), lastSaturday.getMonth(), lastSaturday.getDate(), 23, 59, 59, 999);
          break;
        case 'thisMonth':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
          endDate = new Date(now);
          break;
        default:
          const defaultStart = new Date(today);
          defaultStart.setDate(today.getDate() - 7);
          startDate = new Date(defaultStart.getFullYear(), defaultStart.getMonth(), defaultStart.getDate(), 0, 0, 0, 0);
          endDate = new Date(now);
      }

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;
      if (endDate < startDate) return;

      const formatLocalDateTime = (date) => {
        const localDate = new Date(date);
        const timezoneOffset = localDate.getTimezoneOffset() * 60000;
        const localISOTime = new Date(localDate.getTime() - timezoneOffset);
        return localISOTime.toISOString().slice(0, 19);
      };

      updateFilters({
        ...filters,
        startDate: formatLocalDateTime(startDate),
        endDate: formatLocalDateTime(endDate),
        page: 1
      });
    } catch (error) {}
  };

  const getStatusBadge = (trip) => {
    const status = displayTripStatus(trip);
    const statusConfig = {
      'PENDING': { class: 'badge-warning', text: 'Waiting for Driver' },
      'ASSIGNED': { class: 'badge-info', text: 'Assigned' },
      'STARTED': { class: 'badge-primary border', text: 'Started' },
      'IN_PROGRESS': { class: 'badge-primary', text: 'In Progress' },
      'COMPLETED': { class: 'badge-success', text: 'Completed' },
      'CANCELLED': { class: 'badge-danger', text: 'Cancelled' },
      'DROPPED': { class: 'badge-danger', text: 'Dropped' },
      'CANCELLED_BEFORE_PICKUP': { class: 'badge-danger', text: 'Cancelled - Before Pickup' },
      'CANCELLED_AFTER_PICKUP': { class: 'badge-danger', text: 'Cancelled - After Pickup' },
      'failed': { class: 'badge-info', text: 'Driver NOT Accepted' }
    };
    if (status && status.includes('CANCELLED') && trip.canceledBefore !== undefined) {
      return <span className="badge badge-danger">{trip.canceledBefore ? 'Cancelled - Before Pickup' : 'Cancelled - After Pickup'}</span>;
    }
    const config = statusConfig[status] || { class: 'badge-info', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'CASH': { class: 'badge-success', text: 'Cash' },
      'CARD': { class: 'badge-primary', text: 'Card' },
      'UPI': { class: 'badge-info', text: 'UPI' },
      'WALLET': { class: 'badge-warning', text: 'Wallet' }
    };
    const config = methodConfig[method] || { class: 'badge-info', text: method };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const calculateFare = (trip) => {
    const baseFare = trip.minFare || 0;
    const adjustment = trip.fareAdjustment || 0;
    return baseFare + adjustment;
  };

  const handleExportToExcel = async () => {
    try {
      toast.loading('Preparing export...', { id: 'export' });
      const exportParams = { ...effectiveFilters, limit: 10000, page: 1 };
      const exportResponse = await api.get('/trips', { params: exportParams });
      const exportData = exportResponse?.data?.data?.trips || [];
      if (exportData.length === 0) {
        toast.error('No data available for export with current filters', { id: 'export' });
        return;
      }
      toast.loading('Fetching driver details...', { id: 'export' });
      const driverIds = [...new Set(exportData.map(trip => trip.driverId?._id || trip.driverId).filter(Boolean))];
      let driversMap = {};
      if (driverIds.length > 0) {
        try {
          const driversResponse = await api.get('/drivers', { params: { ids: driverIds.join(','), limit: 1000 } });
          if (driversResponse?.data?.success) {
            driversResponse.data.data.drivers.forEach(driver => { driversMap[driver._id] = driver.name || 'Unknown Driver'; });
          }
        } catch (error) {}
      }
      toast.loading('Generating Excel file...', { id: 'export' });

      const formatLocationXlsx = (location) => {
        let latitude, longitude;
        if (Array.isArray(location) && location.length >= 2) {
          longitude = location[0]; latitude = location[1];
        } else if (location && typeof location === 'object') {
          latitude = location.latitude; longitude = location.longitude;
        } else return 'Location not available';
        if (!latitude || !longitude) return 'Location not available';
        return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      };

      const excelData = exportData.map((trip, index) => {
        const driverName = trip.driverId?.name || driversMap[trip.driverId?._id || trip.driverId] || 'Not Assigned';
        const fare = trip.actualFare || (trip.minFare || 0) + (trip.fareAdjustment || 0);
        let status = displayTripStatus(trip);
        if (status && status.includes('CANCELLED') && trip.canceledBefore !== undefined) {
          status = trip.canceledBefore ? 'Cancelled - Before Pickup' : 'Cancelled - After Pickup';
        } else if (status === 'failed') {
          status = 'Driver NOT Accepted';
        }
        return {
          'S.No': index + 1,
          'Trip ID': trip._id || 'N/A',
          'Passenger Name': trip.bookingForName || 'N/A',
          'Passenger Phone': trip.bookingForPhone || 'N/A',
          'Driver Name': driverName,
          'Driver ID': trip.driverId?._id || trip.driverId || 'N/A',
          'Vehicle Type': carTypeOf(trip) || 'N/A',
          'Start Location': formatLocationXlsx(trip.startLocation),
          'End Location': formatLocationXlsx(trip.endLocation),
          'Status': status || 'N/A',
          'Payment Method': trip.paymentMethod || 'N/A',
          'Payment Status': trip.paymentStatus || 'N/A',
          'Fare (₹)': fare.toFixed(2),
          'Distance (km)': Number(trip.finalDistance) > 0 ? (Math.floor(Number(trip.finalDistance) * 10) / 10).toString() : 'N/A',
          'OTP': trip.otp || 'N/A',
          'Female Only': trip.femaleOnly ? 'Yes' : 'No',
          'Booking Time': trip.bookingTime ? new Date(trip.bookingTime).toLocaleString() : 'N/A',
          'Start Time': trip.startTime ? new Date(trip.startTime).toLocaleString() : 'N/A',
          'End Time': trip.endTime ? new Date(trip.endTime).toLocaleString() : 'N/A',
          'Created Date': trip.bookingTime ? new Date(trip.bookingTime).toLocaleDateString() : 'N/A',
          'Booking App Version': trip.appVersion || 'N/A',
          'Booking Build Number': trip.buildNumber || 'N/A'
        };
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const colWidths = [
        { wch: 8 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
        { wch: 20 }, { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 15 },
        { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 8 },
        { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }
      ];
      worksheet['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Acting Trips Report');
      const currentDate = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Acting_Trips_Report_${currentDate}.xlsx`);
      toast.success(`Exported ${excelData.length} acting trip records successfully!`, { id: 'export' });
    } catch (error) {
      toast.error('Failed to export data. Please try again.', { id: 'export' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="page-title">Acting Trips</h1>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                  <UserCheck className="h-3 w-3 mr-1" />
                  Acting Driver
                </span>
              </div>
              <p className="page-subtitle">
                Monitor and manage acting driver ride trips
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportToExcel}
                className="btn btn-outline btn-sm flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export Excel
              </button>
              <Link to="/trips/active?type=acting" className="btn btn-primary">
                <Car className="h-4 w-4 mr-2" />
                View Active Trips
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 [&>*]:min-w-0">
        {/* Today Rides Card */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Car className="h-4 w-4 text-white" />
                </div>
                <p className="ml-3 text-sm font-medium text-gray-600">Today Rides</p>
              </div>
              <select
                value={todayRidesStatus}
                onChange={(e) => setTodayRidesStatus(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.todayRides || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Yesterday Rides */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-400 rounded-lg flex items-center justify-center">
                  <Car className="h-4 w-4 text-white" />
                </div>
                <p className="ml-3 text-sm font-medium text-gray-600">Yesterday Rides</p>
              </div>
              <select
                value={yesterdayRidesStatus}
                onChange={(e) => setYesterdayRidesStatus(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.yesterdayRides || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Completed Rides */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Car className="h-4 w-4 text-white" />
                </div>
                <p className="ml-3 text-sm font-medium text-gray-600">Completed Rides</p>
              </div>
              <select
                value={completedRidesPeriod}
                onChange={(e) => setCompletedRidesPeriod(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32"
              >
                {periodOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.completedRides || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Total Rides */}
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2 min-h-[48px]">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <Car className="h-4 w-4 text-white" />
                </div>
                <p className="ml-3 text-sm font-medium text-gray-600">Total Rides</p>
              </div>
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <select
                  value={totalRidesPeriod}
                  onChange={(e) => setTotalRidesPeriod(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32"
                >
                  {periodOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={totalRidesStatus}
                  onChange={(e) => setTotalRidesStatus(e.target.value)}
                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-full sm:w-32"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading || statsLoading ? '...' : (stats.totalRides || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6 relative">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="flex items-center gap-2 text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors"
              >
                <h3 className="card-title">Filters</h3>
                {filtersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {/* Acting Driver Badge - Always visible */}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Acting Driver Only
              </span>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('acting-trips-filters');
                clearFilters();
                setPeriod('');
              }}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className={`card-body relative transition-all duration-500 ease-in-out ${filtersExpanded ? 'pb-16' : 'pb-4'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 [&>*]:min-w-0">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by passenger, driver, trip ID..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className={`${getFilterClasses(filters.search)} pl-10`}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className={getFilterClasses(filters.status)}
              >
                <option value="">All Status</option>
                <option value="COMPLETED">Completed</option>
                <option value="DIVERGED">Diverged</option>
                <option value="FINISHED">Completed And Diverged</option>
                <option value="PICKEDUP">Picked up</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="CANCELLED_BEFORE_PICKUP">Cancelled - Before Pickup</option>
                <option value="CANCELLED_AFTER_PICKUP">Cancelled - After Pickup</option>
                <option value="PENDING">Waiting for Driver</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="failed">Driver NOT Accepted</option>
              </select>
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="form-label">Vehicle Type</label>
              <select
                value={filters.vehicleType}
                onChange={(e) => handleFilterChange('vehicleType', e.target.value)}
                className={getFilterClasses(filters.vehicleType)}
              >
                <option value="">All Types</option>
                {/* The customer's car, as an acting booking records it. The ride types this
                    list used to offer (Auto, Bike ...) never occur on an acting trip, so
                    choosing any of them found nothing. */}
                <option value="hatchback">Hatchback</option>
                <option value="sedan">Sedan</option>
                <option value="suv">SUV</option>
                <option value="muv">MUV</option>
                <option value="exsedan">Premium sedan</option>
                <option value="luxury">Luxury</option>
              </select>
            </div>

            {/* Regional Office */}
            <div>
              <label className="form-label">Regional Office</label>
              <select
                value={filters.regionalOffice}
                onChange={(e) => handleFilterChange('regionalOffice', e.target.value)}
                className={getFilterClasses(filters.regionalOffice)}
              >
                <option value="">All Offices</option>
                {regionalOffices.map(office => (
                  <option key={office._id} value={office._id}>{office.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Expanded filters */}
          {filtersExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 [&>*]:min-w-0">
              {/* Payment Method */}
              <div>
                <label className="form-label">Payment Method</label>
                <select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  className={getFilterClasses(filters.paymentMethod)}
                >
                  <option value="">All Methods</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="WALLET">Wallet</option>
                </select>
              </div>

              {/* Payment Status */}
              <div>
                <label className="form-label">Payment Status</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className={getFilterClasses(filters.paymentStatus)}
                >
                  <option value="">All Payment Status</option>
                  <option value="COMPLETED">Payment Completed</option>
                  <option value="NOT_COMPLETED">Payment Pending/Failed</option>
                </select>
              </div>

              {/* Developer Filter */}
              <div>
                <label className="form-label">Developer Filter</label>
                <select
                  value={filters.developerOnly}
                  onChange={(e) => handleFilterChange('developerOnly', e.target.value)}
                  className={getFilterClasses(filters.developerOnly)}
                >
                  <option value="Exclude">Exclude Developers</option>
                  <option value="Include">Include Developers</option>
                  <option value="Only">Developers Only</option>
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="form-label">Quick Date Range</label>
                <select
                  value={period}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  className="input"
                >
                  <option value="">Select Period</option>
                  {periodOptions.filter(o => o.value).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Start Date</label>
                <input
                  type="datetime-local"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className={getFilterClasses(filters.startDate)}
                />
              </div>

              <div>
                <label className="form-label">End Date</label>
                <input
                  type="datetime-local"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className={getFilterClasses(filters.endDate)}
                />
              </div>

              <div className="flex items-center gap-4 pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.femaleOnly}
                    onChange={(e) => handleFilterChange('femaleOnly', e.target.checked)}
                    className={getFilterClasses(filters.femaleOnly, true)}
                  />
                  <span className="text-sm text-gray-600">Female Only</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trips Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-purple-600" />
              Acting Driver Trips
              {isFetching && !isLoading && (
                <span className="text-xs text-gray-400 font-normal">(refreshing...)</span>
              )}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {pagination.totalRecords ? `${pagination.totalRecords} trips` : ''}
              </span>
              <button onClick={handleExportToExcel} className="btn btn-outline btn-sm flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="loading-spinner"></div>
            </div>
          ) : trips.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <UserCheck className="h-12 w-12 mb-4 text-purple-300" />
              <p className="text-lg font-medium">No acting driver trips found</p>
              <p className="text-sm">Try adjusting your filters to find trips</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table min-w-[1200px]">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Trip ID</th>
                    <th className="table-header-cell">Passenger</th>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Location</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Payment</th>
                    <th className="table-header-cell">Fare</th>
                    <th className="table-header-cell">Date</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {trips.map((trip) => (
                    <tr key={trip._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{trip.rideId}</span>
                          {trip.otp && (
                            <span className="text-xs text-blue-600 font-medium">OTP: {trip.otp}</span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{trip.bookingForName || 'N/A'}</span>
                          <span className="text-xs text-gray-500">{trip.bookingForPhone || ''}</span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {trip.driverId?.name || 'Not Assigned'}
                          </span>
                          {trip.driverId?.phone && (
                            <span className="text-xs text-gray-500">{trip.driverId.phone}</span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-info">{carTypeOf(trip) || 'N/A'}</span>
                      </td>
                      <td className="table-cell">
                        <TripLocationViewer
                          startLocation={trip.startLocation}
                          endLocation={trip.endLocation}
                          tripId={trip._id}
                        />
                      </td>
                      <td className="table-cell">{getStatusBadge(trip)}</td>
                      <td className="table-cell">
                        <div className="flex flex-col items-start gap-1">
                          {getPaymentMethodBadge(trip.paymentMethod)}
                          {trip.paymentStatus && (
                            <span className={`badge ${trip.paymentStatus === 'COMPLETED' ? 'badge-success' : 'badge-warning'} text-xs`}>
                              {trip.paymentStatus === 'COMPLETED' ? 'Paid' : 'Pending'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="font-medium text-gray-900">
                          ₹{(trip.actualFare || calculateFare(trip)).toFixed(2)}
                        </span>
                        {Number(trip.finalDistance) > 0 && (
                          <div className="text-xs text-gray-500">
                            {(Math.floor(Number(trip.finalDistance) * 10) / 10)} km
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-xs text-gray-500">
                          {trip.bookingTime ? new Date(trip.bookingTime).toLocaleDateString() : 'N/A'}
                          {trip.bookingTime && (
                            <div>{new Date(trip.bookingTime).toLocaleTimeString()}</div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/trips/${trip._id}`}
                            state={{ from: '/trips/acting' }}
                            className="btn btn-outline btn-sm"
                          >
                            <Eye className="h-3 w-3" />
                          </Link>
                          {/* Every acting trip, not only assigned ones — a trip
                              still searching, or one no driver took, is exactly
                              when the match log is needed. */}
                          <button
                            onClick={() => setRideMatchTrip(trip)}
                            className="btn btn-outline btn-sm"
                            title="Ride Match Log"
                          >
                            <Users className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.total > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Page {pagination.current} of {pagination.total} ({pagination.totalRecords} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page <= 1}
                  className="btn btn-outline btn-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page >= pagination.total}
                  className="btn btn-outline btn-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ride Match Modal */}
      {rideMatchTrip && (
        <RideMatchModal
          tripId={String(rideMatchTrip._id)}
          rideId={rideMatchTrip.rideId}
          onClose={() => setRideMatchTrip(null)}
        />
      )}
    </div>
  );
};

export default ActingTrip;
